<div class="w-full mx-auto min-h-screen p-5 bg-gray-50">
  <!-- Header Section -->
  <div class="bg-white rounded-lg shadow-sm p-6 mb-6">
    <div class="flex justify-between items-center">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 mb-2">
          {{ thirdPartyId ? 'Editar Tercero' : 'Crear Nuevo Tercero' }}
        </h1>
        <p class="text-gray-600">
          {{ thirdPartyId ? 'Modifica la información del tercero' : 'Complete la información del nuevo tercero' }}
        </p>
      </div>
      <button 
        type="button" 
        (click)="goBack()" 
        class="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors">
        <i class="fas fa-arrow-left mr-2"></i>Volver
      </button>
    </div>
  </div>

  <!-- Form Section -->
  <div class="bg-white rounded-lg shadow-sm p-6">
    <form [formGroup]="thirdPartyForm" (ngSubmit)="onSubmit()" class="space-y-6">
      <!-- Información del Tercero -->
      <div class="border border-gray-200 rounded-lg p-6 mb-6">
        <h2 class="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <i class="fas fa-building mr-2 text-blue-500"></i>Información del Tercero
        </h2>
        
        <!-- Mensaje informativo sobre la validación -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div class="flex items-start">
            <i class="fas fa-info-circle text-blue-500 mt-0.5 mr-3"></i>
            <p class="text-sm text-blue-700">
              Para personas jurídicas, ingrese el <strong>Nombre Comercial</strong>. 
              Para personas naturales, ingrese <strong>Nombres y Apellidos</strong>. 
              Opcionalmente puede llenar ambos si desea registrar un contacto para la empresa.
            </p>
          </div>
        </div>

        <!-- Nombre Comercial -->
        <div class="mb-6">
          <label for="commercialName" class="block text-sm font-medium text-gray-700 mb-2">
            Nombre Comercial / Razón Social
          </label>
          <input 
            type="text" 
            id="commercialName" 
            formControlName="commercialName" 
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="Ingrese el nombre comercial o razón social (para personas jurídicas)">
        </div>

        <!-- Nombres y Apellidos -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label for="firstName" class="block text-sm font-medium text-gray-700 mb-2">Nombres</label>
            <input 
              type="text" 
              id="firstName" 
              formControlName="firstName" 
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Ingrese los nombres (persona natural o contacto)">
          </div>
    
          <div>
            <label for="lastName" class="block text-sm font-medium text-gray-700 mb-2">Apellidos</label>
            <input 
              type="text" 
              id="lastName" 
              formControlName="lastName" 
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Ingrese los apellidos (persona natural o contacto)">
          </div>
        </div>

        <!-- Mensaje de error de validación del formulario -->
        <div *ngIf="thirdPartyForm.errors?.['nameRequired'] && (thirdPartyForm.get('commercialName')?.touched || thirdPartyForm.get('firstName')?.touched || thirdPartyForm.get('lastName')?.touched)" 
             class="text-red-500 text-sm mt-3 flex items-center bg-red-50 p-3 rounded-lg">
          <i class="fas fa-exclamation-circle mr-2"></i>
          Debe ingresar un Nombre Comercial o los campos Nombres y Apellidos.
        </div>
      </div>
  
      <!-- Información de Identificación -->
      <div class="border border-gray-200 rounded-lg p-6 mb-6">
        <h2 class="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <i class="fas fa-id-card mr-2 text-green-500"></i>Información de Identificación
        </h2>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label for="typeDocument" class="block text-sm font-medium text-gray-700 mb-2" appRequiredLabel>Tipo de Documento</label>
            <select 
              id="typeDocument" 
              formControlName="typeDocument" 
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors">
              <option value="" disabled>Seleccione un tipo de documento</option>
              <option *ngFor="let type of documentTypes" [value]="type.key">{{ type.value }}</option>
            </select>
            <div *ngIf="thirdPartyForm.get('typeDocument')?.invalid && thirdPartyForm.get('typeDocument')?.touched" 
                 class="text-red-500 text-sm mt-1 flex items-center">
              <i class="fas fa-exclamation-circle mr-1"></i>
              Debe seleccionar un tipo de documento.
            </div>
          </div>

          <div>
            <label for="documentId" class="block text-sm font-medium text-gray-700 mb-2" appRequiredLabel>Número de Documento</label>
            <input 
              type="text" 
              id="documentId" 
              formControlName="documentId" 
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Ingrese el número de documento">
            <div *ngIf="thirdPartyForm.get('documentId')?.invalid && thirdPartyForm.get('documentId')?.touched" 
                 class="text-red-500 text-sm mt-1 flex items-center">
              <i class="fas fa-exclamation-circle mr-1"></i>
              El campo Número de Documento es obligatorio.
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div>
            <label for="expeditionPlace" class="block text-sm font-medium text-gray-700 mb-2">Lugar de Expedición</label>
            <input 
              type="text" 
              id="expeditionPlace" 
              formControlName="expeditionPlace" 
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Ciudad donde se expidió el documento">
          </div>
        </div>
      </div>
  
      <!-- Información de Contacto -->
      <div class="border border-gray-200 rounded-lg p-6 mb-6">
        <h2 class="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <i class="fas fa-envelope mr-2 text-purple-500"></i>Información de Contacto
        </h2>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700 mb-2" appRequiredLabel>Correo Electrónico</label>
            <input 
              type="email" 
              id="email" 
              formControlName="email" 
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="correo@ejemplo.com">
            <div *ngIf="thirdPartyForm.get('email')?.invalid && thirdPartyForm.get('email')?.touched" 
                 class="text-red-500 text-sm mt-1 flex items-center">
              <i class="fas fa-exclamation-circle mr-1"></i>
              El correo debe tener un formato válido.
            </div>
          </div>

          <div>
            <label for="cellPhone" class="block text-sm font-medium text-gray-700 mb-2" appRequiredLabel>Celular</label>
            <input 
              type="tel" 
              id="cellPhone" 
              formControlName="cellPhone" 
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="+57 300 123 4567">
            <div *ngIf="thirdPartyForm.get('cellPhone')?.invalid && thirdPartyForm.get('cellPhone')?.touched" 
                 class="text-red-500 text-sm mt-1 flex items-center">
              <i class="fas fa-exclamation-circle mr-1"></i>
              El celular es obligatorio.
            </div>
          </div>
        </div>
      </div>

      <!-- Información de Ubicación -->
      <div class="border border-gray-200 rounded-lg p-6 mb-6">
        <h2 class="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <i class="fas fa-map-marker-alt mr-2 text-red-500"></i>Información de Ubicación
        </h2>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label for="country" class="block text-sm font-medium text-gray-700 mb-2">País</label>
            <input 
              type="text" 
              id="country" 
              formControlName="country" 
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Colombia">
          </div>

          <div>
            <label for="city" class="block text-sm font-medium text-gray-700 mb-2">Ciudad</label>
            <input 
              type="text" 
              id="city" 
              formControlName="city" 
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Bogotá">
          </div>
        </div>

        <div class="mt-6">
          <label for="addressLine1" class="block text-sm font-medium text-gray-700 mb-2">Dirección Principal</label>
          <input 
            type="text" 
            id="addressLine1" 
            formControlName="addressLine1" 
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="Calle 123 # 45-67">
        </div>

        <div class="mt-6">
          <label for="addressLine2" class="block text-sm font-medium text-gray-700 mb-2">Dirección Complementaria</label>
          <input 
            type="text" 
            id="addressLine2" 
            formControlName="addressLine2" 
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="Apartamento, oficina, edificio (opcional)">
        </div>
      </div>

      <!-- Información Adicional -->
      <div class="border border-gray-200 rounded-lg p-6 mb-6">
        <h2 class="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <i class="fas fa-info-circle mr-2 text-yellow-500"></i>Información Adicional
        </h2>
        
        <div class="mb-6">
          <label for="observations" class="block text-sm font-medium text-gray-700 mb-2">Observaciones</label>
          <textarea 
            id="observations" 
            formControlName="observations" 
            rows="4"
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="Observaciones adicionales sobre el tercero"></textarea>
        </div>

        <div class="flex items-center">
          <input 
            type="checkbox" 
            id="bigRetainers" 
            formControlName="bigRetainers" 
            class="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500">
          <label for="bigRetainers" class="ml-3 text-sm font-medium text-gray-700">
            Grandes Retenedores
          </label>
        </div>
      </div>

      <!-- Botones de Acción -->
      <div class="flex justify-end space-x-4 pt-6 border-t border-gray-200">
        <button 
          type="button" 
          (click)="goBack()" 
          class="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
          Cancelar
        </button>
        <button 
          type="submit" 
          [disabled]="!thirdPartyForm.valid" 
          [class]="thirdPartyForm.valid ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-300 cursor-not-allowed'"
          class="px-6 py-3 text-white rounded-lg transition-colors flex items-center">
          <i class="fas fa-save mr-2"></i>
          {{ thirdPartyId ? 'Actualizar Tercero' : 'Crear Tercero' }}
        </button>
      </div>
    </form>
  </div>
</div>
